import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { apiFetch } from '../utils/api'

const LicenseForm = ({ license, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    userId: '',
    productId: '',
    status: '',
    startDate: '',
    endDate: '',
    source: '',
    externalTransactionId: '',
    receipt: '',
    purchaseToken: '',
    paddleSubscriptionId: ''
  })
  const [users, setUsers] = useState([])
  // List of software products with their plans
  const [softwareProducts, setSoftwareProducts] = useState([])
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    // Load users for selection
    const fetchUsers = async () => {
      try {
        const res = await apiFetch('/api/admin/users')
        const data = await res.json()
        setUsers(data.users || [])
      } catch (err) {
        console.error('Error loading users:', err)
      }
    }
    // Load software products (with plans) for plan selection
    const fetchProducts = async () => {
      try {
        const res = await apiFetch('/api/admin/software-products')
        const data = await res.json()
        setSoftwareProducts(data.software_products || [])
      } catch (err) {
        console.error('Error loading software products for plans:', err)
      }
    }
    fetchUsers()
    fetchProducts()
  }, [])

  useEffect(() => {
    if (isEditing && license) {
      setFormData({
        userId: String(license.userId),
        productId: String(license.productId),
        status: license.status || '',
        startDate: license.startDate ? new Date(license.startDate).toISOString().slice(0,16) : '',
        endDate: license.endDate ? new Date(license.endDate).toISOString().slice(0,16) : '',
        source: license.source || '',
        externalTransactionId: license.externalTransactionId || '',
        receipt: license.platformDetails.receipt || '',
        purchaseToken: license.platformDetails.purchaseToken || '',
        paddleSubscriptionId: license.platformDetails.paddleSubscriptionId || ''
      })
    }
  }, [isEditing, license])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError('')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    // Basic validation
    const { userId, productId, status, startDate, source, externalTransactionId } = formData
    if (!userId || !productId || !status || !startDate || !source || !externalTransactionId) {
      setError('Please fill in all required fields')
      setIsLoading(false)
      return
    }
    const payload = {
      userId: Number.parseInt(formData.userId, 10),
      productId: formData.productId,
      status: formData.status,
      startDate: new Date(formData.startDate).toISOString(),
      endDate: formData.endDate ? new Date(formData.endDate).toISOString() : null,
      source: formData.source,
      externalTransactionId: formData.externalTransactionId,
      platformDetails: {
        receipt: formData.receipt,
        purchaseToken: formData.purchaseToken,
        paddleSubscriptionId: formData.paddleSubscriptionId
      }
    }
    onSave(payload)
    setIsLoading(false)
  }

  return (
    <div className="user-form-overlay">
      <div className="user-form-container">
        <div className="user-form-header">
          <h3>{isEditing ? 'Edit License' : 'Add New License'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        {error && <div className="error-message">{error}</div>}
        <form className="user-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="userId">User</label>
            <select id="userId" name="userId" value={formData.userId} onChange={handleChange} required>
              <option value="">Select user...</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.email}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="productId">Product Plan</label>
            <select id="productId" name="productId" value={formData.productId} onChange={handleChange} required>
              <option value="">Select plan...</option>
              {softwareProducts.map(prod => prod.plans && prod.plans.length > 0 && (
                <optgroup key={prod.id} label={prod.name}>
                  {prod.plans.map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <input type="text" id="status" name="status" value={formData.status} onChange={handleChange} placeholder="e.g. active" required />
          </div>
          <div className="form-group">
            <label htmlFor="startDate">Start Date</label>
            <input type="datetime-local" id="startDate" name="startDate" value={formData.startDate} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="endDate">End Date</label>
            <input type="datetime-local" id="endDate" name="endDate" value={formData.endDate} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="source">Source</label>
            <select id="source" name="source" value={formData.source} onChange={handleChange} required>
              <option value="">Select source...</option>
              <option value="paddle">paddle</option>
              <option value="beta">beta</option>
              <option value="appstore">appstore</option>
              <option value="googleplay">googleplay</option>
              <option value="manual">manual</option>
              <option value="internal">internal</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="externalTransactionId">Transaction ID</label>
            <input type="text" id="externalTransactionId" name="externalTransactionId" value={formData.externalTransactionId} onChange={handleChange} required />
          </div>
          <h4>Platform Details (optional)</h4>
          <div className="form-group">
            <label htmlFor="receipt">Receipt</label>
            <input type="text" id="receipt" name="receipt" value={formData.receipt} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="purchaseToken">Purchase Token</label>
            <input type="text" id="purchaseToken" name="purchaseToken" value={formData.purchaseToken} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="paddleSubscriptionId">Paddle Subscription ID</label>
            <input type="text" id="paddleSubscriptionId" name="paddleSubscriptionId" value={formData.paddleSubscriptionId} onChange={handleChange} />
          </div>
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>Cancel</button>
            <button type="submit" className="btn-save" disabled={isLoading}>{isEditing ? 'Update' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

LicenseForm.propTypes = {
  license: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isEditing: PropTypes.bool
}

export default LicenseForm 